import React, { useState } from 'react';
import { Check, Circle, RotateCcw, Square, Zap } from 'lucide-react';
import type { C3MockIncident } from '../../mock/c3';
import { CAMP_OF, STATE_META } from '../../mock/c3';
import Dialog from '../../ui/Dialog';
import { useToast } from '../../app/ToastProvider';
import { formatUtcTime } from '../../lib/time';

interface IncidentSummaryModalProps {
  incident: C3MockIncident;
  onClose: () => void;
  onOpenRemediation: () => void;
  onOpenCampaign?: (campaignId: string) => void;
  onApprove?: (incident: C3MockIncident) => void;
}

function chainStatus(s: string) {
  if (s === 'done') return <span className="text-c3-green inline-flex items-center gap-1 text-xs font-bold justify-self-end"><Check size={12} strokeWidth={2} aria-hidden />DONE</span>;
  if (s === 'progress') return <span className="text-c3-blue inline-flex items-center gap-1 text-xs font-bold justify-self-end"><Circle size={12} strokeWidth={2} aria-hidden />IN PROGRESS</span>;
  if (s === 'pending') return <span className="text-c3-amber inline-flex items-center gap-1 text-xs font-bold justify-self-end"><Square size={12} strokeWidth={2} aria-hidden />AWAITING DECISION</span>;
  return <span className="text-c3-red inline-flex items-center gap-1 text-xs font-bold justify-self-end"><RotateCcw size={12} strokeWidth={2} aria-hidden />REVERTED</span>;
}

const IncidentSummaryModal: React.FC<IncidentSummaryModalProps> = ({
  incident,
  onClose,
  onOpenRemediation,
  onOpenCampaign,
  onApprove,
}) => {
  const { notify } = useToast();
  const [local, setLocal] = useState(incident);
  const d = local.detail;
  const st = STATE_META[local.state] || STATE_META.progress;
  const campId = CAMP_OF[local.id];

  const handleApprove = () => {
    if (onApprove) {
      onApprove(local);
      return;
    }
    const now = new Date();
    const t = formatUtcTime(now);
    const next: C3MockIncident = {
      ...local,
      state: 'progress',
      detail: {
        ...d,
        chain: d.chain.map(([label, status]) => [label, status === 'pending' ? 'progress' : status]),
        audit: [...d.audit, [t, 'hum', 'Remediation APPROVED (one-click)']],
      },
    };
    setLocal(next);
    setTimeout(() => {
      setLocal((prev) => ({
        ...prev,
        state: 'auto',
        detail: {
          ...prev.detail,
          chain: prev.detail.chain.map(([label]) => [label, 'done']),
          audit: [...prev.detail.audit, [t, 'ok', 'Approved steps executed and validated']],
        },
      }));
      notify({
        tone: 'success',
        title: 'Executed.',
        message: 'Approved remediation completed, validated, and written to the audit trail.',
      });
    }, 1800);
  };

  return (
    <Dialog
      onClose={onClose}
      title={
        <>
          {local.title}
          {local.xdom && (
            <span className="inline-block text-[12px] font-bold text-c3-purple border border-c3-purple rounded px-1.5 ml-2 align-middle tracking-wider">
              CROSS-DOMAIN
            </span>
          )}
        </>
      }
    >
      <div className="text-c3-mute text-sm mb-[18px]">
        {local.id} · {local.source} · {local.asset} · <b className="text-c3-white">{st.label}</b>
        {campId && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[12px] font-bold text-c3-red border border-c3-red rounded px-1.5 ml-2 align-middle tracking-wider cursor-pointer bg-transparent"
            onClick={() => onOpenCampaign?.(campId)}
          >
            <Zap size={12} strokeWidth={2} aria-hidden />{campId}
          </button>
        )}
      </div>

      <div className="my-[18px]">
        <h4 className="text-xs font-bold text-c3-mute tracking-widest mb-2.5">
          CORRELATED SIGNALS ({d.signals.length})
        </h4>
        {d.signals.map((s, i) => (
          <div
            key={i}
            className="grid grid-cols-[78px_130px_1fr] gap-3 bg-c3-tile border border-c3-tile-bd rounded-lg px-3.5 py-2.5 mb-1.5 text-[13px]"
          >
            <span className="text-c3-mute">{s[0]}</span>
            <span className="text-c3-lpurple font-bold">{s[1]}</span>
            <span>{s[2]}</span>
          </div>
        ))}
      </div>

      <div className="my-[18px]">
        <h4 className="text-xs font-bold text-c3-mute tracking-widest mb-2.5">AI REASONING</h4>
        <div className="bg-acc/5 border border-c3-purple rounded-xl px-4 py-3.5 text-sm">
          {d.reasoning}
          <span className="block text-c3-purple font-bold text-[13px] mt-2.5">
            Confidence: {d.confidence.toFixed(2)}
            {local.fp ? ' · Suppressed, retained for review' : ''}
          </span>
          {d.attack.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {d.attack.map((a) => (
                <span
                  key={a}
                  className="text-xs text-c3-blue border border-c3-blue rounded-md px-2 py-0.5"
                >
                  {a}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {d.chain.length > 0 && (
        <div className="my-[18px]">
          <h4 className="text-xs font-bold text-c3-mute tracking-widest mb-2.5">REMEDIATION CHAIN</h4>
          {d.chain.map((c, i) => (
            <div
              key={i}
              className="grid grid-cols-[30px_1fr_170px] gap-3 items-center bg-c3-tile border border-c3-tile-bd rounded-lg px-3.5 py-2.5 mb-1.5 text-sm"
            >
              <span className="bg-c3-purple-d text-on-acc w-[26px] h-[26px] rounded-md flex items-center justify-center font-bold text-[13px]">
                {i + 1}
              </span>
              <span>{c[0]}</span>
              {chainStatus(c[1])}
            </div>
          ))}
        </div>
      )}

      <div className="my-[18px]">
        <h4 className="text-xs font-bold text-c3-mute tracking-widest mb-2.5">
          PROVENANCE — EVIDENCE LINEAGE
        </h4>
        <p className="text-c3-mute text-xs mb-2.5">
          Source → signal → incident lineage retained for audit and replay.
        </p>
        <div className="font-mono text-[12px] text-t3 flex flex-wrap gap-1.5 items-center mb-1.5">
          <span className="bg-s2 border border-prog/40 text-c3-blue rounded px-2 py-0.5">
            src:{local.source.split('→')[0].trim()}
          </span>
          <span>→</span>
          <span className="bg-s2 border border-c3-purple text-c3-lpurple rounded px-2 py-0.5">
            sig:{d.signals.length}
          </span>
          <span>→</span>
          <span className="bg-s2 border border-need/40 text-c3-amber rounded px-2 py-0.5">
            {local.id}
          </span>
        </div>
      </div>

      <div className="my-[18px]">
        <h4 className="text-xs font-bold text-c3-mute tracking-widest mb-2.5">
          AUDIT TRAIL (immutable · exportable)
        </h4>
        <div className="font-mono text-[13px] bg-s2 border border-c3-tile-bd rounded-lg px-3.5 py-3 text-c3-mute max-h-[220px] overflow-y-auto">
          {d.audit.map((a, i) => {
            const cls = a[1] === 'ok' ? 'text-c3-green' : a[1] === 'hum' ? 'text-c3-blue' : 'text-c3-purple';
            const who = a[1] === 'ok' ? 'EXEC' : a[1] === 'hum' ? 'HUMAN' : 'AI';
            return (
              <div key={i} className="my-0.5">
                <span className="text-t3">[{a[0]}]</span>{' '}
                <span className={cls}>{who}</span> {a[2]}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-5 items-center">
        <button type="button" className="c3-btn c3-btn-approve" onClick={onOpenRemediation}>
          Open remediation workspace
        </button>
        {local.state === 'awaiting' && (
          <button type="button" className="c3-btn c3-btn-ghost" onClick={handleApprove}>
            <Check size={16} strokeWidth={1.5} aria-hidden className="inline -mt-0.5 mr-1" />Approve remediation
          </button>
        )}
        {local.fp && (
          <button
            type="button"
            className="c3-btn c3-btn-ghost"
            onClick={() =>
              notify({ title: 'Reopened.', message: 'Suppression rule flagged for retraining; incident moved to the active queue.' })
            }
          >
            Reopen as incident
          </button>
        )}
        <button type="button" className="c3-btn c3-btn-ghost" onClick={onClose}>
          Close
        </button>
      </div>
    </Dialog>
  );
};

export default IncidentSummaryModal;
